import type { ScriptableLineSegmentContext } from 'chart.js';
import { useMemo } from 'react';

import {
  CHART_GRAPH,
  CHART_TOOLTIP_TARGET_TYPE,
} from '@/entities/chart/model/constants';
import useChartColor from '@/features/(charts)/chart-renderer/hooks/use-chart-color';
import useChartLabel from '@/features/(charts)/chart-renderer/hooks/use-chart-label';
import chartUtils from '@/features/(charts)/chart-renderer/lib/chart-utils';
import { getAlphaColor } from '@/styles/colors/color.utils';
import { getTimezoneDayjs } from '@/utils/time';

type Props = {
  chart: ChartBase;
  tableData: ChartTableRes;
};

const useChartTimeSeriesData = (props: Props) => {
  const { chart } = props;
  const { target, field } = chart;
  const { term, is_compare } = target;
  const tableData: ChartCrossTableRes = chartUtils.getSortedRowItems(
    chart,
    props.tableData,
    chart.selected_field.items || [],
  ) as ChartCrossTableRes;
  const getLabel = useChartLabel(tableData.field_item_meta);
  const getColor = useChartColor(field);

  const today = getTimezoneDayjs();

  const dateRange = chartUtils.getTimeRangeByTerm(
    today,
    props.chart.target.term,
  );

  const compareRange = chartUtils.getCompareTimeRangeByTerm(
    today,
    props.chart.target.term,
    props.chart.target.compare_term,
  );

  const dateLabels = useMemo(
    () =>
      chartUtils.getDateLabelsByUnit({
        unit: term.unit,
        dateRange,
      }),
    [dateRange, term.unit],
  );

  const compareDateLabels = useMemo(
    () =>
      chartUtils.getDateLabelsByUnit({
        unit: term.unit,
        dateRange: compareRange,
      }),
    [compareRange, term.unit],
  );

  const timeSeriesChartData = useMemo(() => {
    const frequency = chartUtils.getFrequencyByTermUnit(term.unit);

    let datasets: any = [];
    tableData.tables[0]?.forEach((segment, index) => {
      const dateMap: Record<string, { x: string; y: number }> = {};
      const compareDateMap: Record<string, { x: string; y: number }> = {};
      const computedColor = getColor(segment.x);

      dateLabels.forEach((label) => {
        dateMap[label] = {
          x: label,
          y: 0,
        };
      });

      compareDateLabels.forEach((label) => {
        compareDateMap[label] = {
          x: label,
          y: 0,
        };
      });

      segment.values?.forEach((data) => {
        const date = chartUtils.getFormatByFrequency(frequency)(
          getTimezoneDayjs(data.y as string),
        );

        if (dateMap[date]) {
          dateMap[date].y = data.value || 0;
        }
      });

      const data = Object.values(dateMap);

      datasets.push({
        date_type: CHART_TOOLTIP_TARGET_TYPE.CURRENT,
        data,
        borderColor: computedColor,
        backgroundColor: getAlphaColor(
          computedColor,
          chart.graph === CHART_GRAPH.AREA_STACKED ? 0.4 : 1,
        ),
        label: getLabel(field.type, segment.x),
        fill: chart.graph === CHART_GRAPH.AREA_STACKED ? true : false,

        segment: {
          borderDash: (ctx: ScriptableLineSegmentContext) => {
            // 마지막 포인트가 오늘기준 날짜단위와 같을경우 점선표시

            const { end } = chartUtils.parseDateFormatByFrequency(
              ctx.p1.raw.x as string,
              frequency,
            );

            if (getTimezoneDayjs(end).isAfter()) {
              return [6, 6];
            }
            return undefined;
          },
        },
        spanGaps: true,
        xAxisID: 'x',
        meta: {
          x: segment.x,
          y: dateLabels,
        },
      });

      if (is_compare) {
        tableData.compare_tables?.[0]?.[index]?.values?.forEach((data) => {
          const date = chartUtils.getFormatByFrequency(frequency)(
            getTimezoneDayjs(data.y as string),
          );
          if (compareDateMap[date]) {
            compareDateMap[date].y = data.value || 0;
          }
        });
        datasets.push({
          date_type: CHART_TOOLTIP_TARGET_TYPE.COMPARE,
          data: Object.values(compareDateMap),
          borderColor: getAlphaColor(computedColor, 0.4),
          backgroundColor: getAlphaColor(computedColor, 0.4),
          label: getLabel(field.type, segment.x),
          segment: {
            borderDash: (ctx: ScriptableLineSegmentContext) => {
              // 마지막 포인트가 오늘기준 날짜단위와 같을경우 점선표시
              const { end } = chartUtils.parseDateFormatByFrequency(
                ctx.p1.raw.x as string,
                frequency,
              );
              if (getTimezoneDayjs(end).isAfter()) {
                return [6, 6];
              }
              return undefined;
            },
          },
          spanGaps: true,
          xAxisID: 'x1',
          meta: {
            x: segment.x,
            y: compareDateLabels,
          },
        });
      }
    });

    return {
      datasets: datasets,
    };
  }, [
    term.unit,
    tableData.tables,
    tableData.compare_tables,
    getColor,
    dateLabels,
    compareDateLabels,
    chart.graph,
    getLabel,
    field.type,
    is_compare,
  ]);

  return timeSeriesChartData;
};

export default useChartTimeSeriesData;
